import { callable } from './callable'
import {
  putMutation,
  type EduBoardingPayload,
  type EduAttendanceMarkPayload,
  type HelperMutationRecord,
} from './helper/helperOutboxDb'
import { assertMutableWorkspace } from './helper/mutationGuard'

export interface SetHelperEduJobsInput {
  workspaceId: string
  targetUid: string
  eduJobs: ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]
  eduRouteIds?: string[]
  eduRollSessionIds?: string[]
}

export interface SetHelperEduJobsResult {
  success: boolean
  eduJobs: ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]
  eduRouteIds: string[]
  eduRollSessionIds: string[]
}

export interface GetMyEduJobsInput {
  workspaceId: string
}

export interface GetMyEduJobsResult {
  eduJobs: ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]
  eduRouteIds: string[]
  eduRollSessionIds: string[]
  routes: Array<{ id: string; name: string }>
  sessions: Array<{ id: string; name: string }>
}

export interface RecordPickupInput {
  workspaceId: string
  learnerId: string
  collector: {
    guardianUid?: string
    name?: string
  }
}

export interface RecordPickupResult {
  success: boolean
  pickupId: string
}

export interface GrantPickupPermissionInput {
  workspaceId: string
  learnerId: string
  collectorName: string
  note?: string
}

export interface GrantPickupPermissionResult {
  success: boolean
  permissionId: string
  validDate: string
}

export interface ApprovePickupByOfficeInput {
  workspaceId: string
  learnerId: string
  collectorName: string
  note?: string
}

export interface ApprovePickupByOfficeResult {
  success: boolean
  permissionId: string
  pickupId: string
}

export interface GateLearnerGuardian {
  uid: string
  displayName: string
  relation: string
}

export interface GateLearner {
  learnerId: string
  displayName: string
  classOrBatch: string
  guardians: GateLearnerGuardian[]
}

export interface ListGateLearnersInput {
  workspaceId: string
}

export interface ListGateLearnersResult {
  learners: GateLearner[]
}

export interface TransportStop {
  id: string
  name: string
  order: number
  learnerIds: string[]
}

export interface UpsertEduTransportRouteInput {
  workspaceId: string
  routeId?: string
  name: string
  code?: string
  stops: TransportStop[]
  helperUids: string[]
}

export interface UpsertEduTransportRouteResult {
  success: boolean
  routeId: string
}

export interface ArchiveEduTransportRouteInput {
  workspaceId: string
  routeId: string
}

export interface ArchiveEduTransportRouteResult {
  success: boolean
  routeId: string
}

export interface EduTransportRoute {
  id: string
  workspaceId: string
  name: string
  code?: string | null
  stops: TransportStop[]
  helperUids: string[]
  archived?: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
}

export interface ListEduTransportRoutesInput {
  workspaceId: string
  includeArchived?: boolean
}

export interface ListEduTransportRoutesResult {
  routes: EduTransportRoute[]
}

export interface GetHelperEduJobsInput {
  workspaceId: string
  helperUid: string
}

export interface GetHelperEduJobsResult {
  eduJobs: ('ROLL' | 'GATE' | 'BUS' | 'DESK')[]
  eduRouteIds: string[]
  eduRollSessionIds: string[]
}

export interface OpenRouteRunInput {
  workspaceId: string
  routeId: string
  date: string
  leg: 'AM' | 'PM'
}

export interface OpenRouteRunResult {
  runId: string
  status: string
  created?: boolean
}

export interface MarkBoardingInput {
  workspaceId: string
  runId: string
  learnerId: string
  status: 'BOARDED' | 'NOT_HERE'
  clientMutationId?: string
}

export interface MarkBoardingResult {
  success: boolean
  boardingId: string
  status: string
}

export interface SendRouteLateNoticeInput {
  workspaceId: string
  runId: string
  delayMinutes?: number
  message?: string
}

export interface SendRouteLateNoticeResult {
  success: boolean
  notifiedCount: number
}

export interface CloseRouteRunInput {
  workspaceId: string
  runId: string
}

export interface CloseRouteRunResult {
  success: boolean
  notBoardedLearnerIds: string[]
}

export interface LogVisitorInput {
  workspaceId: string
  name: string
  reason: string
  meetingWhom: string
  passNo?: string
}

export interface LogVisitorResult {
  visitorId: string
  status: string
}

export interface CheckOutVisitorInput {
  workspaceId: string
  visitorId: string
}

export interface CheckOutVisitorResult {
  success: boolean
  visitorId: string
}

export interface LogWalkInEnquiryInput {
  workspaceId: string
  visitorId?: string
  contact: {
    name: string
    phone?: string
    email?: string
  }
  learner: {
    name: string
    isMinor: boolean
    dob?: string
  }
  interestUseCase: 'TEACHING' | 'TRAINING'
  interestNodeId?: string | null
  reason?: string
  meetingWhom?: string
  passNo?: string
  requestId?: string
}

export interface LogWalkInEnquiryResult {
  success: boolean
  enquiryId: string
  visitorId: string
}

export const setHelperEduJobs = async (input: SetHelperEduJobsInput): Promise<SetHelperEduJobsResult> => {
  return callable<SetHelperEduJobsInput, SetHelperEduJobsResult>('setHelperEduJobs', input)
}

export const getHelperEduJobs = async (input: GetHelperEduJobsInput): Promise<GetHelperEduJobsResult> => {
  return callable<GetHelperEduJobsInput, GetHelperEduJobsResult>('getHelperEduJobs', input)
}

export const getMyEduJobs = async (input: GetMyEduJobsInput): Promise<GetMyEduJobsResult> => {
  return callable<GetMyEduJobsInput, GetMyEduJobsResult>('getMyEduJobs', input)
}

export const recordPickup = async (input: RecordPickupInput): Promise<RecordPickupResult> => {
  return callable<RecordPickupInput, RecordPickupResult>('recordPickup', input)
}

export const grantPickupPermission = async (input: GrantPickupPermissionInput): Promise<GrantPickupPermissionResult> => {
  return callable<GrantPickupPermissionInput, GrantPickupPermissionResult>('grantPickupPermission', input)
}

export const approvePickupByOffice = async (input: ApprovePickupByOfficeInput): Promise<ApprovePickupByOfficeResult> => {
  return callable<ApprovePickupByOfficeInput, ApprovePickupByOfficeResult>('approvePickupByOffice', input)
}

export const listGateLearners = async (input: ListGateLearnersInput): Promise<ListGateLearnersResult> => {
  return callable<ListGateLearnersInput, ListGateLearnersResult>('listGateLearners', input)
}

export const upsertEduTransportRoute = async (input: UpsertEduTransportRouteInput): Promise<UpsertEduTransportRouteResult> => {
  return callable<UpsertEduTransportRouteInput, UpsertEduTransportRouteResult>('upsertEduTransportRoute', input)
}

export const archiveEduTransportRoute = async (input: ArchiveEduTransportRouteInput): Promise<ArchiveEduTransportRouteResult> => {
  return callable<ArchiveEduTransportRouteInput, ArchiveEduTransportRouteResult>('archiveEduTransportRoute', input)
}

export const listEduTransportRoutes = async (input: ListEduTransportRoutesInput): Promise<ListEduTransportRoutesResult> => {
  return callable<ListEduTransportRoutesInput, ListEduTransportRoutesResult>('listEduTransportRoutes', input)
}

export const openRouteRun = async (input: OpenRouteRunInput): Promise<OpenRouteRunResult> => {
  return callable<OpenRouteRunInput, OpenRouteRunResult>('openRouteRun', input)
}

export const markBoarding = async (input: MarkBoardingInput): Promise<MarkBoardingResult> => {
  return callable<MarkBoardingInput, MarkBoardingResult>('markBoarding', input)
}

export const sendRouteLateNotice = async (input: SendRouteLateNoticeInput): Promise<SendRouteLateNoticeResult> => {
  return callable<SendRouteLateNoticeInput, SendRouteLateNoticeResult>('sendRouteLateNotice', input)
}

export const closeRouteRun = async (input: CloseRouteRunInput): Promise<CloseRouteRunResult> => {
  return callable<CloseRouteRunInput, CloseRouteRunResult>('closeRouteRun', input)
}

export const logVisitor = async (input: LogVisitorInput): Promise<LogVisitorResult> => {
  return callable<LogVisitorInput, LogVisitorResult>('logVisitor', input)
}

export const checkOutVisitor = async (input: CheckOutVisitorInput): Promise<CheckOutVisitorResult> => {
  return callable<CheckOutVisitorInput, CheckOutVisitorResult>('checkOutVisitor', input)
}

export const logWalkInEnquiry = async (input: LogWalkInEnquiryInput): Promise<LogWalkInEnquiryResult> => {
  return callable<LogWalkInEnquiryInput, LogWalkInEnquiryResult>('logWalkInEnquiry', input)
}

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `boarding-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const queueEduBoarding = async (data: EduBoardingPayload): Promise<HelperMutationRecord> => {
  await assertMutableWorkspace(data.workspaceId)
  const now = Date.now()
  const record: HelperMutationRecord = {
    id: generateId(),
    type: 'EDU_BOARDING',
    payload: data,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
  }
  await putMutation(record)
  return record
}

export const queueEduAttendanceMark = async (data: EduAttendanceMarkPayload): Promise<HelperMutationRecord> => {
  await assertMutableWorkspace(data.workspaceId)
  const now = Date.now()
  const record: HelperMutationRecord = {
    id: generateId(),
    type: 'EDU_ATTENDANCE_MARK',
    payload: data,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
  }
  await putMutation(record)
  return record
}
